/* Shared UI primitives — atoms used across every screen.
 *
 * Polish rules baked in here:
 * · Touch targets ≥ 40 px on tap-only controls.
 * · Border-radius snaps to the R scale in tokens.js.
 * · Shadows snap to the SHADOW scale in tokens.js (neutral default;
 *   brand-tinted shadow opt-in via Card `elevation="brand"`).
 * · Spacing snaps to a 2/4/6/8/10/12/14/16/20/24 grid.
 */
import React, { useState, useRef } from 'react';
import { C, R, SHADOW } from '../tokens.js';

/* Bottom navigation */
export const BNav = ({ tab, onTab }) => {
  const tabs = [{ id: 'home', l: 'Home' }, { id: 'subscriptions', l: 'Routine' }, { id: 'orders', l: 'Orders' }, { id: 'account', l: 'Account' }];
  const icons = {
    home:   <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M2 11L11 2l9 9M4 9.5V19a1 1 0 001 1h4v-5h4v5h4a1 1 0 001-1V9.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>,
    orders: <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M8 5H6a2 2 0 00-2 2v11a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /><rect x="8" y="3" width="6" height="4" rx="1" stroke="currentColor" strokeWidth="1.8" /><path d="M8 12h6M8 15.5h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>,
    subscriptions: <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><rect x="3" y="4" width="16" height="16" rx="3" stroke="currentColor" strokeWidth="1.8" /><path d="M15 2v4M7 2v4M3 10h16M7 14.5h8M7 17.5h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>,
    account:<svg width="22" height="22" viewBox="0 0 22 22" fill="none"><circle cx="11" cy="7.5" r="3.5" stroke="currentColor" strokeWidth="1.8" /><path d="M4 19.5c0-3.866 3.134-7 7-7s7 3.134 7 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>,
  };
  return (
    <div style={{ height: 78, background: C.white, borderTop: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', paddingBottom: 12, flexShrink: 0, boxShadow: SHADOW.bottomBar }}>
      {tabs.map(t => {
        const active = tab === t.id;
        return (
          <button key={t.id} onClick={() => onTab(t.id)} style={{ flex: 1, height: 60, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, padding: 0, background: 'none', border: 'none', cursor: 'pointer', color: active ? C.primary : C.textLight, transition: 'color .15s' }}>
            {icons[t.id]}
            <span style={{ fontSize: 10, fontWeight: active ? 700 : 600, letterSpacing: 0.1 }}>{t.l}</span>
          </button>
        );
      })}
    </div>
  );
};

/* Card. Default uses a neutral layered shadow; `elevation="brand"` keeps
 * the orange-tinted shadow for hero/CTA surfaces. `flat` for nested cards. */
export const Card = ({ children, style, onClick, elevation = 'card' }) => {
  const shadow = elevation === 'brand' ? SHADOW.brandCard : elevation === 'raised' ? SHADOW.raised : elevation === 'flat' ? SHADOW.none : SHADOW.card;
  return (
    <div onClick={onClick} style={{ background: C.white, borderRadius: R.xl, padding: 16, boxShadow: shadow, border: `1px solid ${C.border}`, cursor: onClick ? 'pointer' : undefined, ...style }}>{children}</div>
  );
};

/* Button. Primary gets a soft tinted shadow so it pops above cards. */
export const Btn = ({ children, onClick, full, sm, disabled, outline, danger }) => {
  const bg = outline ? 'transparent' : danger ? C.error : (disabled ? '#d4f1f7' : C.primary);
  const color = outline ? (danger ? C.error : C.primary) : (disabled ? C.textLight : C.white);
  const border = outline ? `1.5px solid ${danger ? C.error : C.primary}` : 'none';
  const shadow = !outline && !disabled
    ? (danger ? '0 1px 2px rgba(220,38,38,.18), 0 6px 14px rgba(220,38,38,.20)' : '0 1px 2px rgba(12,192,223,.18), 0 6px 14px rgba(12,192,223,.22)')
    : 'none';
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background: bg,
      color,
      border,
      borderRadius: R.lg,
      padding: sm ? '9px 16px' : '13px 22px',
      fontSize: sm ? 13 : 15,
      fontWeight: 700,
      letterSpacing: 0.1,
      cursor: disabled ? 'not-allowed' : 'pointer',
      width: full ? '100%' : undefined,
      minHeight: sm ? 36 : 48,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      boxShadow: shadow,
      transition: 'box-shadow .15s, transform .15s',
    }}>{children}</button>
  );
};

/* Back-header. Back button is 40×40 (iOS-grade touch target). */
export const BkHdr = ({ title, onBack, right, bg }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: bg || C.bg, borderBottom: `1px solid ${C.border}`, flexShrink: 0, gap: 8 }}>
    <button onClick={onBack} style={{ width: 40, height: 40, borderRadius: R.lg, background: C.white, border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M11 13L7 9l4-4" stroke={C.text} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
    </button>
    <span style={{ fontSize: 16, fontWeight: 700, color: C.text, flex: 1, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', letterSpacing: -0.1 }}>{title}</span>
    <div style={{ minWidth: 40, display: 'flex', justifyContent: 'flex-end' }}>{right || null}</div>
  </div>
);

export const Badge = ({ children, bg, color, sm }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', padding: sm ? '2px 7px' : '3px 9px', borderRadius: R.pill, background: bg || '#e6f7fb', color: color || C.primary, fontSize: sm ? 10 : 12, fontWeight: 700, whiteSpace: 'nowrap', lineHeight: 1.4, letterSpacing: 0.1 }}>{children}</span>
);

export const Chip = ({ children, active, onClick }) => (
  <button onClick={onClick} style={{ padding: '7px 14px', borderRadius: R.pill, border: `1.5px solid ${active ? C.primary : C.border}`, background: active ? C.primary : C.white, color: active ? C.white : C.textMed, fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all .15s', flexShrink: 0, minHeight: 34 }}>{children}</button>
);

export const SPill = ({ status }) => {
  const m = {
    active: ['#dcfce7', '#16a34a', 'Active'],
    paused: ['#fef3c7', '#d97706', 'Paused'],
    cancelled: ['#fee2e2', '#dc2626', 'Cancelled'],
    delivered: ['#dcfce7', '#16a34a', 'Delivered'],
    out_for_delivery: ['#dbeafe', '#2563eb', 'On the way'],
    confirmed: ['#dcfce7', '#16a34a', 'Confirmed'],
    completed: ['#f1f5f9', '#64748b', 'Completed'],
    preparing: ['#fef3c7', '#d97706', 'Preparing'],
    pending_payment: ['#fef3c7', '#d97706', 'Pending'],
  };
  const [bg, c, l] = m[status] || ['#f1f5f9', '#64748b', status];
  return <Badge bg={bg} color={c} sm>{l}</Badge>;
};

export const Stepper = ({ value, onChange, min = 1, max = 20 }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <button onClick={() => onChange(Math.max(min, value - 1))} aria-label="Decrease" style={{ width: 32, height: 32, borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, fontSize: 18, fontWeight: 700, cursor: 'pointer', color: C.text, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>−</button>
    <span style={{ fontSize: 15, fontWeight: 700, color: C.text, minWidth: 22, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    <button onClick={() => onChange(Math.min(max, value + 1))} aria-label="Increase" style={{ width: 32, height: 32, borderRadius: R.sm, border: 'none', background: C.primary, fontSize: 18, fontWeight: 700, cursor: 'pointer', color: C.white, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, boxShadow: '0 1px 2px rgba(12,192,223,.18)' }}>+</button>
  </div>
);

export const Toggle = ({ val, onChange }) => (
  <button onClick={() => onChange(!val)} role="switch" aria-checked={!!val} style={{ width: 46, height: 26, borderRadius: 13, background: val ? C.primary : '#e5e7eb', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background .2s', flexShrink: 0, padding: 0 }}>
    <div style={{ width: 20, height: 20, borderRadius: 10, background: C.white, position: 'absolute', top: 3, left: val ? 23 : 3, transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.18)' }} />
  </button>
);

export const Divider = ({ my = 12 }) => <div style={{ height: 1, background: C.border, margin: `${my}px 0` }} />;

export const SHead = ({ title, action, onAction }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 8 }}>
    <span style={{ fontSize: 16, fontWeight: 800, color: C.text, letterSpacing: -0.1 }}>{title}</span>
    {action && <button onClick={onAction} style={{ background: 'none', border: 'none', color: C.primary, fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: '6px 8px', margin: '-6px -8px', letterSpacing: 0.1 }}>{action}</button>}
  </div>
);

/* 6-digit OTP input */
export const OTPBox = ({ onComplete }) => {
  const [d, setD] = useState(['', '', '', '', '', '']);
  const refs = useRef(Array.from({ length: 6 }, () => React.createRef()));
  const focus = (i) => refs.current[i]?.current?.focus();
  const handleChange = (i, val) => {
    const digit = val.replace(/\D/g, '').slice(-1);
    const next = [...d]; next[i] = digit; setD(next);
    if (digit && i < 5) focus(i + 1);
    if (next.every(x => x)) onComplete(next.join(''));
  };
  const handleKey = (i, e) => { if (e.key === 'Backspace') { if (d[i]) { const n = [...d]; n[i] = ''; setD(n); } else if (i > 0) { focus(i - 1); const n = [...d]; n[i - 1] = ''; setD(n); } } };
  const handlePaste = (e) => { const p = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6); if (!p) return; const next = Array(6).fill('').map((_, i) => p[i] || ''); setD(next); focus(Math.min(p.length, 5)); if (p.length >= 6) onComplete(p.slice(0, 6)); e.preventDefault(); };
  return (
    <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
      {d.map((digit, i) => (
        <input key={i} ref={refs.current[i]} value={digit} onChange={(e) => handleChange(i, e.target.value)} onKeyDown={(e) => handleKey(i, e)} onPaste={handlePaste}
          maxLength={1} inputMode="numeric" autoComplete="one-time-code"
          style={{ width: 46, height: 56, borderRadius: R.lg, textAlign: 'center', fontSize: 24, fontWeight: 800, color: C.text, border: `2px solid ${digit ? C.primary : C.border}`, background: C.white, outline: 'none', transition: 'border .15s', fontVariantNumeric: 'tabular-nums' }} />
      ))}
    </div>
  );
};

/* Image with emoji fallback */
export const ImgOrEmoji = ({ src, fallback, size = 60, radius = R.xl, bg = '#e6f7fb', fontSize, style = {} }) => {
  const [err, setErr] = useState(false);
  const fs = fontSize || Math.round(size * 0.48);
  if (src && !err) return <img src={src} alt="" onError={() => setErr(true)} style={{ width: size, height: size, borderRadius: radius, objectFit: 'cover', flexShrink: 0, display: 'block', ...style }} />;
  return <div style={{ width: size, height: size, borderRadius: radius, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: fs, flexShrink: 0, ...style }}>{fallback}</div>;
};

export const StarPicker = ({ value, onChange }) => (
  <div style={{ display: 'flex', gap: 6 }}>
    {[1, 2, 3, 4, 5].map(n => (
      <button key={n} onClick={() => onChange(n)} aria-label={`${n} star${n>1?'s':''}`} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, fontSize: 28, color: n <= value ? '#d97706' : '#e5e7eb', transition: 'color .15s', lineHeight: 1 }}>★</button>
    ))}
  </div>
);

export const RatingBars = ({ reviews }) => {
  const dist = [0, 0, 0, 0, 0];
  reviews.forEach(r => { if (r.rating >= 1 && r.rating <= 5) dist[5 - r.rating]++; });
  const max = Math.max(...dist, 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {['5', '4', '3', '2', '1'].map((lbl, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: C.textMed, width: 10, textAlign: 'right' }}>{lbl}</span>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="#d97706"><polygon points="5,0.5 6.5,3.5 10,3.9 7.5,6.3 8.2,9.7 5,8 1.8,9.7 2.5,6.3 0,3.9 3.5,3.5" /></svg>
          <div style={{ flex: 1, height: 6, background: '#f5f5f4', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ width: `${(dist[i] / max) * 100}%`, height: '100%', background: '#d97706', borderRadius: 3, transition: 'width .5s' }} />
          </div>
          <span style={{ fontSize: 11, color: C.textMed, width: 14, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{dist[i]}</span>
        </div>
      ))}
    </div>
  );
};

/* Persistent footer pill — appears when routine basket is non-empty.
   When sitting above the bottom nav (tabs visible), bottom: 86; else 16. */
export const RoutineBasketPill = ({ count, onOpen, aboveNav }) => (
  <button onClick={onOpen} style={{
    position: 'absolute',
    left: 16, right: 16,
    bottom: aboveNav ? 86 : 16,
    height: 52,
    borderRadius: R.lg,
    background: C.primary,
    color: C.white,
    border: 'none',
    cursor: 'pointer',
    padding: '0 18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: '0 4px 14px rgba(12,192,223,.32), 0 1px 3px rgba(12,192,223,.18)',
    fontSize: 14,
    fontWeight: 700,
    zIndex: 50,
  }}>
    <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ fontSize: 18 }}>📅</span>
      <span>{count} product{count > 1 ? 's' : ''} · Build routine</span>
    </span>
    <span style={{ fontSize: 18 }}>→</span>
  </button>
);

export const ReviewCard = ({ r, onHelpful }) => {
  const [helped, setHelped] = useState(false);
  return (
    <div style={{ padding: '14px 0', borderBottom: `1px solid ${C.border}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, gap: 12 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 0, flex: 1 }}>
          <div style={{ width: 36, height: 36, borderRadius: 18, background: `linear-gradient(135deg,${C.primary},${C.accent})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, fontWeight: 800, flexShrink: 0 }}>{r.avatar}</div>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{r.name}</span>
              {r.verified && <span style={{ fontSize: 10, background: '#dcfce7', color: '#15803d', padding: '2px 6px', borderRadius: R.pill, fontWeight: 700 }}>✓ Verified</span>}
            </div>
            <span style={{ fontSize: 11, color: C.textMed }}>{r.date}</span>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: 1 }}>{[1, 2, 3, 4, 5].map(n => <span key={n} style={{ fontSize: 12, color: n <= r.rating ? '#d97706' : '#e5e7eb' }}>★</span>)}</div>
          {r.productTag && <span style={{ fontSize: 10, background: '#e6f7fb', color: C.primary, padding: '2px 7px', borderRadius: R.pill, fontWeight: 700 }}>{r.productTag}</span>}
        </div>
      </div>
      <p style={{ fontSize: 13, color: C.text, lineHeight: 1.55, marginBottom: 10 }}>{r.text}</p>
      <button onClick={() => { if (!helped) { setHelped(true); onHelpful(r.id); } }} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: `1px solid ${helped ? C.primary : C.border}`, borderRadius: R.sm, padding: '6px 10px', cursor: 'pointer', color: helped ? C.primary : C.textMed, fontSize: 12, fontWeight: 700, transition: 'all .15s' }}>
        👍 Helpful {r.helpful + (helped ? 1 : 0)}
      </button>
    </div>
  );
};
